import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/AdminLayout';
import {
  Box,
  Paper,
  Typography,
  Chip,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Avatar,
  Badge,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  Tabs,
  Tab,
  alpha,
} from '@mui/material';
import {
  Visibility,
  Person,
  Refresh,
  DirectionsBike,
  AdminPanelSettings,
  Store,
  FiberManualRecord,
  Groups,
  PersonSearch,
} from '@mui/icons-material';

interface StaffSession {
  id: string;
  user_name: string;
  user_email: string;
  user_role: 'rider' | 'cashier' | 'staff' | 'manager' | 'admin' | 'customer';
  is_active: boolean;
  last_activity: string;
  started_at: string;
  duration_display: string;
  is_online: boolean;
  status?: string;
  last_login?: string;
  minutes_since_login?: number;
}

interface ActivitySummary {
  total_staff: number;
  online_staff: number;
  offline_staff: number;
  riders_online: number;
  riders_total: number;
  pos_users_online: number;
  pos_users_total: number;
  admins_online: number;
  admins_total: number;
  customers_online: number;
  customers_total: number;
}

// Helper: parse Firestore timestamp to Date
const parseTimestamp = (ts: any): Date | null => {
  if (!ts) return null;
  if (typeof ts.toDate === 'function') return ts.toDate();
  if (typeof ts === 'string') {
    const d = new Date(ts);
    return isNaN(d.getTime()) ? null : d;
  }
  if (ts.seconds) return new Date(ts.seconds * 1000);
  const d = new Date(ts);
  return isNaN(d.getTime()) ? null : d;
};

// Helper: format "time ago" from minutes
const formatTimeAgo = (minutes: number): string => {
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${Math.round(minutes)}m ago`;
  if (minutes < 1440) {
    const h = Math.floor(minutes / 60);
    const m = Math.round(minutes % 60);
    return m > 0 ? `${h}h ${m}m ago` : `${h}h ago`;
  }
  const days = Math.floor(minutes / 1440);
  return days === 1 ? '1 day ago' : `${days} days ago`;
};

const StaffMonitoringPage: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<ActivitySummary | null>(null);
  const [sessions, setSessions] = useState<StaffSession[]>([]);
  const [selectedSession, setSelectedSession] = useState<StaffSession | null>(null);
  const [sessionDialogOpen, setSessionDialogOpen] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [activeTab, setActiveTab] = useState(0); // 0=All, 1=Staff, 2=Riders, 3=Customers
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);

  const fetchMonitoringData = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      setError(null);

      const { collection, getDocs, query, where } = await import('../../services/firestoreCompat');
      const { db, auth } = await import('../../services/firebaseConfig');

      const allSessions: StaffSession[] = [];
      const now = new Date();
      const processedEmails = new Set<string>();

      // --- Riders from drivers collection ---
      try {
        const driversRef = collection(db, 'drivers');
        const driversSnapshot = await getDocs(query(driversRef, where('is_active', '==', true)));

        for (const driverDoc of driversSnapshot.docs) {
          const data = driverDoc.data();
          const email = data.email || '';
          if (!email || email.includes('test') || email.includes('mock') || email.includes('dummy')) continue;

          const lastLoginDate = parseTimestamp(data.last_login) || parseTimestamp(data.created_at);
          if (!lastLoginDate) continue;

          processedEmails.add(email.toLowerCase());

          let isOnline = false;
          let status = data.status || 'offline';

          // Check Realtime DB for rider status
          try {
            const safeEmail = email.replace(/@/g, '_').replace(/\./g, '_');
            const user = auth.currentUser;
            if (user) {
              const token = await user.getIdToken();
              const url = `https://__SAHEEH_RTDB_UNSET__/drivers/${safeEmail}.json?auth=${token}`;
              const response = await fetch(url);
              if (response.ok) {
                const realtimeData = await response.json();
                if (realtimeData?.status) {
                  status = realtimeData.status;
                  isOnline = status === 'available' || status === 'busy';
                }
              }
            }
          } catch (e) {
            // silently fail - use Firestore status
          }

          const minutesSinceLogin = (now.getTime() - lastLoginDate.getTime()) / (1000 * 60);
          if (minutesSinceLogin > 15) {
            isOnline = false;
            status = 'offline';
          }

          allSessions.push({
            id: driverDoc.id,
            user_name: data.name || 'Unknown Rider',
            user_email: email,
            user_role: 'rider',
            is_active: data.is_active !== false,
            last_activity: lastLoginDate.toISOString(),
            started_at: lastLoginDate.toISOString(),
            duration_display: formatTimeAgo(minutesSinceLogin),
            is_online: isOnline,
            status,
            last_login: lastLoginDate.toISOString(),
            minutes_since_login: Math.round(minutesSinceLogin),
          });
        }
      } catch (e) {
        console.warn('[MONITORING] Failed to fetch drivers:', e);
      }

      // --- Users (cashiers, managers, admins) ---
      try {
        const usersRef = collection(db, 'users');
        const usersSnapshot = await getDocs(usersRef);

        for (const userDoc of usersSnapshot.docs) {
          const data = userDoc.data();
          const email = data.email || '';
          if (!email || email.includes('test') || email.includes('mock') || email.includes('dummy') || email.includes('temp-')) continue;

          const lastLoginDate = parseTimestamp(data.last_login);
          if (!lastLoginDate) continue;

          let role = (data.role || 'cashier').toLowerCase();
          if (role === 'staff') role = 'cashier';

          processedEmails.add(email.toLowerCase());

          const minutesSinceLogin = (now.getTime() - lastLoginDate.getTime()) / (1000 * 60);
          const isOnline = minutesSinceLogin <= 15;

          allSessions.push({
            id: userDoc.id,
            user_name: data.name || email.split('@')[0] || 'Unknown User',
            user_email: email,
            user_role: role as any,
            is_active: data.is_active !== false,
            last_activity: lastLoginDate.toISOString(),
            started_at: lastLoginDate.toISOString(),
            duration_display: formatTimeAgo(minutesSinceLogin),
            is_online: isOnline,
            last_login: lastLoginDate.toISOString(),
            minutes_since_login: Math.round(minutesSinceLogin),
          });
        }
      } catch (e) {
        console.warn('[MONITORING] Failed to fetch users:', e);
      }

      // --- Customers ---
      try {
        const customersRef = collection(db, 'customers');
        const customersSnapshot = await getDocs(customersRef);

        for (const customerDoc of customersSnapshot.docs) {
          const data = customerDoc.data();
          const email = data.email || '';
          if (!email || email.includes('test') || email.includes('mock') || email.includes('dummy') || email.includes('temp-')) continue;
          if (processedEmails.has(email.toLowerCase())) continue;

          const lastLoginDate = parseTimestamp(data.last_login);
          if (!lastLoginDate) continue;

          processedEmails.add(email.toLowerCase());

          const minutesSinceLogin = (now.getTime() - lastLoginDate.getTime()) / (1000 * 60);
          const isOnline = minutesSinceLogin <= 15;

          const firstName = data.first_name || '';
          const lastName = data.last_name || '';
          const fullName = data.full_name || `${firstName} ${lastName}`.trim() || email.split('@')[0];

          allSessions.push({
            id: customerDoc.id,
            user_name: fullName,
            user_email: email,
            user_role: 'customer',
            is_active: data.is_active !== false,
            last_activity: lastLoginDate.toISOString(),
            started_at: lastLoginDate.toISOString(),
            duration_display: formatTimeAgo(minutesSinceLogin),
            is_online: isOnline,
            last_login: lastLoginDate.toISOString(),
            minutes_since_login: Math.round(minutesSinceLogin),
          });
        }
      } catch (e) {
        console.warn('[MONITORING] Failed to fetch customers:', e);
      }

      // Sort: online first, then by most recent activity
      allSessions.sort((a, b) => {
        if (a.is_online !== b.is_online) return a.is_online ? -1 : 1;
        return new Date(b.last_activity).getTime() - new Date(a.last_activity).getTime();
      });

      // Build summary
      const staffOnly = allSessions.filter(s => s.user_role !== 'customer');
      const riders = allSessions.filter(s => s.user_role === 'rider');
      const posUsers = allSessions.filter(s => s.user_role === 'cashier' || s.user_role === 'manager');
      const admins = allSessions.filter(s => s.user_role === 'admin');
      const customers = allSessions.filter(s => s.user_role === 'customer');

      if (isMountedRef.current) {
        setSummary({
          total_staff: staffOnly.length,
          online_staff: staffOnly.filter(s => s.is_online).length,
          offline_staff: staffOnly.filter(s => !s.is_online).length,
          riders_online: riders.filter(s => s.is_online).length,
          riders_total: riders.length,
          pos_users_online: posUsers.filter(s => s.is_online).length,
          pos_users_total: posUsers.length,
          admins_online: admins.filter(s => s.is_online).length,
          admins_total: admins.length,
          customers_online: customers.filter(s => s.is_online).length,
          customers_total: customers.length,
        });
        setSessions(allSessions);
        setLastRefresh(new Date());
      }
    } catch (error) {
      console.error('[MONITORING] Failed to fetch monitoring data:', error);
      if (isMountedRef.current) setError('Failed to load monitoring data. Please try again.');
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, []);

  // Debounced fetch for real-time listeners
  const debouncedFetch = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchMonitoringData(false);
    }, 2000);
  }, [fetchMonitoringData]);

  useEffect(() => {
    isMountedRef.current = true;
    fetchMonitoringData();
    return () => { isMountedRef.current = false; };
  }, [fetchMonitoringData]);

  // Real-time listeners (debounced to avoid excessive re-renders)
  useEffect(() => {
    let unsubscribeUsers: (() => void) | null = null;
    let unsubscribeDrivers: (() => void) | null = null;
    let unsubscribeCustomers: (() => void) | null = null;
    let isFirst = { users: true, drivers: true, customers: true };

    const setup = async () => {
      try {
        const { collection, query, where, onSnapshot } = await import('../../services/firestoreCompat');
        const { db } = await import('../../services/firebaseConfig');

        unsubscribeUsers = onSnapshot(collection(db, 'users'), () => {
          if (isFirst.users) { isFirst.users = false; return; }
          debouncedFetch();
        }, () => {});

        unsubscribeDrivers = onSnapshot(
          query(collection(db, 'drivers'), where('is_active', '==', true)),
          () => {
            if (isFirst.drivers) { isFirst.drivers = false; return; }
            debouncedFetch();
          }, () => {}
        );

        unsubscribeCustomers = onSnapshot(collection(db, 'customers'), () => {
          if (isFirst.customers) { isFirst.customers = false; return; }
          debouncedFetch();
        }, () => {});
      } catch (e) {
        console.warn('[MONITORING] Failed to setup real-time listeners:', e);
      }
    };

    setup();
    return () => {
      if (unsubscribeUsers) unsubscribeUsers();
      if (unsubscribeDrivers) unsubscribeDrivers();
      if (unsubscribeCustomers) unsubscribeCustomers();
    };
  }, [debouncedFetch]);

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'rider': return <DirectionsBike fontSize="small" />;
      case 'admin': return <AdminPanelSettings fontSize="small" />;
      case 'cashier':
      case 'staff':
      case 'manager': return <Store fontSize="small" />;
      case 'customer': return <Person fontSize="small" />;
      default: return <Person fontSize="small" />;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'rider': return '#2E7D32';
      case 'admin': return '#1565C0';
      case 'cashier':
      case 'staff': return '#E65100';
      case 'manager': return '#6A1B9A';
      case 'customer': return '#00838F';
      default: return '#757575';
    }
  };

  const getRoleLabel = (role: string): string => {
    switch (role) {
      case 'rider': return t('users.rider');
      case 'admin': return t('users.admin');
      case 'cashier': return t('users.cashier');
      case 'staff': return t('users.cashier');
      case 'manager': return t('users.manager');
      case 'customer': return t('users.customer');
      default: return role;
    }
  };

  // Filtered sessions based on active tab
  const filteredSessions = sessions.filter(s => {
    switch (activeTab) {
      case 0: return true; // All
      case 1: return s.user_role === 'cashier' || s.user_role === 'manager' || s.user_role === 'admin' || s.user_role === 'staff';
      case 2: return s.user_role === 'rider';
      case 3: return s.user_role === 'customer';
      default: return true;
    }
  });

  const tabCounts = {
    all: sessions.length,
    staff: sessions.filter(s => s.user_role === 'cashier' || s.user_role === 'manager' || s.user_role === 'admin' || s.user_role === 'staff').length,
    riders: sessions.filter(s => s.user_role === 'rider').length,
    customers: sessions.filter(s => s.user_role === 'customer').length,
  };

  const handleViewSession = (session: StaffSession) => {
    setSelectedSession(session);
    setSessionDialogOpen(true);
  };

  const renderSkeletonRows = () => (
    Array.from({ length: 5 }).map((_, i) => (
      <TableRow key={i}>
        <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}><Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}><Skeleton variant="circular" width={36} height={36} /><Box><Skeleton width={120} height={18} /><Skeleton width={160} height={14} /></Box></Box></TableCell>
        <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}><Skeleton width={60} height={24} sx={{ borderRadius: 2 }} /></TableCell>
        <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}><Skeleton width={70} height={24} sx={{ borderRadius: 2 }} /></TableCell>
        <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}><Skeleton width={140} height={18} /></TableCell>
        <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}><Skeleton width={80} height={18} /></TableCell>
        <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, py: { xs: 1, md: 1.5 }, px: { xs: 1, md: 2 } }}><Skeleton variant="circular" width={28} height={28} /></TableCell>
      </TableRow>
    ))
  );

  // Stat card helper
  const StatCard = ({ icon, label, value, sub, color }: { icon: React.ReactNode; label: string; value: string | number; sub?: string; color: string }) => (
    <Paper sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid #3a3d52', boxShadow: 'none', height: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: alpha(color, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', color }}>
          {icon}
        </Box>
        <Typography variant="caption" sx={{ color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          {label}
        </Typography>
      </Box>
      <Typography variant="h4" sx={{ fontWeight: 700, color: '#e0e0e0', letterSpacing: '-0.02em' }}>
        {value}
      </Typography>
      {sub && <Typography variant="caption" sx={{ color: '#6b7280' }}>{sub}</Typography>}
    </Paper>
  );

  const headerCellSx = { fontWeight: 700, fontSize: '0.75rem', color: '#9ca3af', py: 1.5, letterSpacing: '0.03em' };

  return (
    <AdminLayout>
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#e0e0e0', letterSpacing: '-0.02em' }}>
              {t('monitoring.title')}
            </Typography>
            <Typography variant="body2" sx={{ color: '#9ca3af', mt: 0.5 }}>
              {t('monitoring.subtitle')}
              {lastRefresh && <> &middot; Updated {lastRefresh.toLocaleTimeString()}</>}
            </Typography>
          </Box>
          <Tooltip title={t('common.refresh')}>
            <IconButton
              onClick={() => fetchMonitoringData()}
              sx={{ border: '1px solid #3a3d52', borderRadius: 2 }}
            >
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

        {/* Stats Grid */}
        {summary && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', lg: 'repeat(6, 1fr)' }, gap: 2, mb: 3 }}>
            <StatCard icon={<FiberManualRecord sx={{ fontSize: 18 }} />} label={t('monitoring.onlineNow')} value={summary.online_staff} sub={`${t('monitoring.of')} ${summary.total_staff} ${t('monitoring.staff')}`} color="#4CAF50" />
            <StatCard icon={<Groups sx={{ fontSize: 20 }} />} label={t('monitoring.totalStaff')} value={summary.total_staff} sub={`${summary.offline_staff} ${t('common.offline').toLowerCase()}`} color="#1565C0" />
            <StatCard icon={<DirectionsBike sx={{ fontSize: 20 }} />} label={t('monitoring.ridersActive')} value={`${summary.riders_online}/${summary.riders_total}`} sub={`${summary.riders_online} ${t('common.active').toLowerCase()}`} color="#2E7D32" />
            <StatCard icon={<Store sx={{ fontSize: 20 }} />} label={t('monitoring.cashiersOnDuty')} value={`${summary.pos_users_online}/${summary.pos_users_total}`} sub={t('monitoring.cashiersAndManagers')} color="#E65100" />
            <StatCard icon={<AdminPanelSettings sx={{ fontSize: 20 }} />} label={t('monitoring.adminsOnline')} value={`${summary.admins_online}/${summary.admins_total}`} sub={`${summary.admins_online} ${t('common.active').toLowerCase()}`} color="#1565C0" />
            <StatCard icon={<Person sx={{ fontSize: 20 }} />} label={t('monitoring.customersOnline')} value={`${summary.customers_online}/${summary.customers_total}`} sub={`${summary.customers_online} ${t('common.active').toLowerCase()}`} color="#00838F" />
          </Box>
        )}

        {/* Tabs + Table */}
        <Paper sx={{ borderRadius: 2.5, border: '1px solid #3a3d52', boxShadow: 'none', overflow: 'hidden' }}>
          <Box sx={{ borderBottom: '1px solid #3a3d52', px: { xs: 1.5, md: 3 }, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              sx={{
                minHeight: 48,
                '& .MuiTab-root': { minHeight: 48, textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', py: 0 },
                '& .MuiTabs-indicator': { height: 3, borderRadius: 2 },
              }}
            >
              <Tab label={`${t('common.all')} (${tabCounts.all})`} />
              <Tab label={`${t('monitoring.staff')} (${tabCounts.staff})`} />
              <Tab label={`${t('monitoring.riders')} (${tabCounts.riders})`} />
              <Tab label={`${t('monitoring.customers')} (${tabCounts.customers})`} />
            </Tabs>
            <Chip
              icon={<FiberManualRecord sx={{ fontSize: '10px !important', color: filteredSessions.filter(s => s.is_online).length > 0 ? '#4CAF50 !important' : '#bdbdbd !important' }} />}
              label={`${filteredSessions.filter(s => s.is_online).length} ${t('common.online').toLowerCase()}`}
              size="small"
              sx={{ fontWeight: 600, bgcolor: '#f8f8f8', color: '#9ca3af' }}
            />
          </Box>

          {loading && sessions.length === 0 ? (
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'rgba(0, 200, 150,0.08)' }}>
                    <TableCell sx={headerCellSx}>{t('monitoring.staffMember').toUpperCase()}</TableCell>
                    <TableCell sx={headerCellSx}>{t('users.role').toUpperCase()}</TableCell>
                    <TableCell sx={headerCellSx}>{t('common.status').toUpperCase()}</TableCell>
                    <TableCell sx={headerCellSx}>{t('monitoring.lastActive').toUpperCase()}</TableCell>
                    <TableCell sx={headerCellSx}>{t('monitoring.timeAgo').toUpperCase()}</TableCell>
                    <TableCell sx={{ ...headerCellSx, width: 56 }} />
                  </TableRow>
                </TableHead>
                <TableBody>{renderSkeletonRows()}</TableBody>
              </Table>
            </TableContainer>
          ) : filteredSessions.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <PersonSearch sx={{ fontSize: 56, color: '#ddd', mb: 1.5 }} />
              <Typography variant="body1" sx={{ color: '#6b7280', fontWeight: 500 }}>
                {activeTab === 0 ? t('monitoring.noStaff') : t('monitoring.noTypeFound', { type: [t('common.all'), t('monitoring.staff'), t('monitoring.riders'), t('monitoring.customers')][activeTab] })}
              </Typography>
            </Box>
          ) : (
            <>
            {/* Mobile Card View */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1.5, p: 1.5 }}>
              {filteredSessions.map((session) => {
                const roleColor = getRoleColor(session.user_role);
                return (
                  <Box
                    key={session.id}
                    onClick={() => handleViewSession(session)}
                    sx={{
                      backgroundColor: '#252838',
                      border: '1px solid #3a3d52',
                      borderRadius: '10px',
                      p: 1.5,
                      cursor: 'pointer',
                      '&:active': { bgcolor: '#2a2d42' },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: 1 }}>
                        <Badge
                          overlap="circular"
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                          variant="dot"
                          sx={{ '& .MuiBadge-badge': { bgcolor: session.is_online ? '#4CAF50' : '#bdbdbd', border: '2px solid #252838', width: 10, height: 10, borderRadius: '50%' } }}
                        >
                          <Avatar sx={{ bgcolor: alpha(roleColor, 0.12), color: roleColor, width: 36, height: 36 }}>
                            {getRoleIcon(session.user_role)}
                          </Avatar>
                        </Badge>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#fff', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {session.user_name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6b7280', lineHeight: 1.2, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {session.user_email}
                          </Typography>
                        </Box>
                        <Chip
                          label={getRoleLabel(session.user_role)}
                          size="small"
                          sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600, bgcolor: alpha(roleColor, 0.1), color: roleColor, border: `1px solid ${alpha(roleColor, 0.2)}`, flexShrink: 0 }}
                        />
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FiberManualRecord sx={{ fontSize: 10, color: session.is_online ? '#4CAF50' : '#bdbdbd' }} />
                        <Typography variant="caption" sx={{ fontWeight: 600, color: session.is_online ? '#4CAF50' : '#999' }}>
                          {session.is_online ? t('common.online') : t('common.offline')}
                        </Typography>
                        {session.status && session.user_role === 'rider' && session.status !== 'offline' && (
                          <Chip label={session.status} size="small" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600, bgcolor: session.status === 'available' ? alpha('#4CAF50', 0.15) : alpha('#FF9800', 0.15), color: session.status === 'available' ? '#2E7D32' : '#E65100' }} />
                        )}
                        <Typography variant="caption" sx={{ color: '#9ca3af', ml: 0.5 }}>
                          {session.last_login ? new Date(session.last_login).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Never'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: session.is_online ? '#00C896' : '#999' }}>
                          {session.duration_display}
                        </Typography>
                        <IconButton size="small" sx={{ color: '#bbb', width: 28, height: 28 }}>
                          <Visibility sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* Desktop Table View */}
            <TableContainer sx={{ overflowX: 'auto', display: { xs: 'none', md: 'block' } }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'rgba(0, 200, 150,0.08)' }}>
                    <TableCell sx={headerCellSx}>{t('monitoring.staffMember').toUpperCase()}</TableCell>
                    <TableCell sx={headerCellSx}>{t('users.role').toUpperCase()}</TableCell>
                    <TableCell sx={headerCellSx}>{t('common.status').toUpperCase()}</TableCell>
                    <TableCell sx={headerCellSx}>{t('monitoring.lastActive').toUpperCase()}</TableCell>
                    <TableCell sx={headerCellSx}>{t('monitoring.timeAgo').toUpperCase()}</TableCell>
                    <TableCell sx={{ ...headerCellSx, width: 56 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredSessions.map((session) => {
                    const roleColor = getRoleColor(session.user_role);
                    return (
                      <TableRow
                        key={session.id}
                        sx={{
                          '&:hover': { bgcolor: alpha('#1565C0', 0.03) },
                          transition: 'background-color 0.15s',
                          cursor: 'pointer',
                        }}
                        onClick={() => handleViewSession(session)}
                      >
                        <TableCell sx={{ py: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Badge
                              overlap="circular"
                              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                              variant="dot"
                              sx={{
                                '& .MuiBadge-badge': {
                                  bgcolor: session.is_online ? '#4CAF50' : '#bdbdbd',
                                  border: '2px solid #252838',
                                  width: 12,
                                  height: 12,
                                  borderRadius: '50%',
                                }
                              }}
                            >
                              <Avatar sx={{ bgcolor: alpha(roleColor, 0.12), color: roleColor, width: 42, height: 42 }}>
                                {getRoleIcon(session.user_role)}
                              </Avatar>
                            </Badge>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#e0e0e0', lineHeight: 1.4 }}>
                                {session.user_name}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#6b7280', lineHeight: 1.2, display: 'block' }}>
                                {session.user_email}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          <Chip
                            label={getRoleLabel(session.user_role)}
                            size="small"
                            sx={{
                              height: 26,
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              bgcolor: alpha(roleColor, 0.1),
                              color: roleColor,
                              border: `1px solid ${alpha(roleColor, 0.2)}`,
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FiberManualRecord sx={{ fontSize: 12, color: session.is_online ? '#4CAF50' : '#bdbdbd' }} />
                            <Typography variant="body2" sx={{ fontWeight: 600, color: session.is_online ? '#2E7D32' : '#999' }}>
                              {session.is_online ? t('common.online') : t('common.offline')}
                            </Typography>
                            {session.status && session.user_role === 'rider' && session.status !== 'offline' && (
                              <Chip
                                label={session.status}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: '0.68rem',
                                  fontWeight: 600,
                                  ml: 0.5,
                                  bgcolor: session.status === 'available' ? alpha('#4CAF50', 0.15) : alpha('#FF9800', 0.15),
                                  color: session.status === 'available' ? '#2E7D32' : '#E65100',
                                }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                            {session.last_login ? new Date(session.last_login).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Never'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: session.is_online ? '#2E7D32' : '#999' }}>
                            {session.duration_display}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          <IconButton size="small" sx={{ color: '#bbb', '&:hover': { color: '#1565C0', bgcolor: alpha('#1565C0', 0.08) } }}>
                            <Visibility sx={{ fontSize: 20 }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            </>
          )}
        </Paper>

        {/* Session Details Dialog */}
        <Dialog
          open={sessionDialogOpen}
          onClose={() => setSessionDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          {selectedSession && (() => {
            const rc = getRoleColor(selectedSession.user_role);
            return (
              <>
                <DialogTitle sx={{ pb: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Badge
                      overlap="circular"
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      variant="dot"
                      sx={{ '& .MuiBadge-badge': { bgcolor: selectedSession.is_online ? '#4CAF50' : '#bdbdbd', border: '2px solid #252838', width: 12, height: 12, borderRadius: '50%' } }}
                    >
                      <Avatar sx={{ bgcolor: alpha(rc, 0.12), color: rc, width: 48, height: 48 }}>
                        {getRoleIcon(selectedSession.user_role)}
                      </Avatar>
                    </Badge>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                        {selectedSession.user_name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                        {selectedSession.user_email}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <FiberManualRecord sx={{ fontSize: 12, color: selectedSession.is_online ? '#4CAF50' : '#bdbdbd' }} />
                      <Typography variant="body2" sx={{ fontWeight: 600, color: selectedSession.is_online ? '#2E7D32' : '#999' }}>
                        {selectedSession.is_online ? t('common.online') : t('common.offline')}
                      </Typography>
                    </Box>
                  </Box>
                </DialogTitle>
                <DialogContent>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
                    {[
                      { label: t('users.role'), content: <Chip label={getRoleLabel(selectedSession.user_role)} size="small" sx={{ height: 24, fontWeight: 600, bgcolor: alpha(rc, 0.1), color: rc }} /> },
                      { label: t('monitoring.riderStatus'), content: selectedSession.status && selectedSession.user_role === 'rider' ? <Chip label={selectedSession.status} size="small" sx={{ height: 24, fontWeight: 600, bgcolor: selectedSession.status === 'available' ? alpha('#4CAF50', 0.15) : alpha('#FF9800', 0.15), color: selectedSession.status === 'available' ? '#2E7D32' : '#E65100' }} /> : <Typography variant="body2" sx={{ color: '#ccc' }}>N/A</Typography> },
                      { label: t('monitoring.lastLogin'), content: <Typography variant="body2" sx={{ fontWeight: 500, color: '#e0e0e0' }}>{selectedSession.last_login ? new Date(selectedSession.last_login).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : t('common.never')}</Typography> },
                      { label: t('monitoring.timeSinceLogin'), content: <Typography variant="body2" sx={{ fontWeight: 600, color: selectedSession.is_online ? '#2E7D32' : '#999' }}>{selectedSession.duration_display}</Typography> },
                      { label: t('monitoring.minutesElapsed'), content: <Typography variant="body2" sx={{ fontWeight: 500, color: '#e0e0e0' }}>{selectedSession.minutes_since_login !== undefined ? `${selectedSession.minutes_since_login.toLocaleString()} min` : 'N/A'}</Typography> },
                      { label: t('monitoring.accountActive'), content: <Chip label={selectedSession.is_active ? t('common.active') : t('common.inactive')} size="small" sx={{ height: 24, fontWeight: 600, bgcolor: selectedSession.is_active ? alpha('#4CAF50', 0.1) : alpha('#d32f2f', 0.1), color: selectedSession.is_active ? '#2E7D32' : '#d32f2f' }} /> },
                    ].map((item, i) => (
                      <Box key={i} sx={{ p: 1.5, bgcolor: 'rgba(0, 200, 150,0.08)', borderRadius: 2 }}>
                        <Typography variant="caption" sx={{ color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', mb: 0.5 }}>
                          {item.label}
                        </Typography>
                        {item.content}
                      </Box>
                    ))}
                  </Box>
                </DialogContent>
                <DialogActions sx={{ px: { xs: 1.5, md: 3 }, pb: 2.5 }}>
                  <Button onClick={() => setSessionDialogOpen(false)} variant="outlined" sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
                    {t('common.close')}
                  </Button>
                </DialogActions>
              </>
            );
          })()}
        </Dialog>
      </Box>
    </AdminLayout>
  );
};

export default StaffMonitoringPage;
